'use client';

/**
 * Canvas card registry — `content_key` (from matrix) → React component.
 *
 * Single source of truth for "what does each canvas card render?"
 * Mirrors the rail-side `INSIGHTS_CARD_REGISTRY` exactly — same
 * primitive, both surfaces. Adding a card to canvas = one matrix
 * entry on the backend (`CANVAS_CARDS` in `total_matrix.py`) + one
 * entry here.
 *
 * For Phase 1 of the cards-everywhere prototype the registry reuses
 * the rail's `DetailsCard` for the `rfi_details` content key — same
 * card primitive renders on both surfaces, proving the unification.
 *
 * @see klyde_edge/ui_config/total_matrix.py — CANVAS_CARDS
 * @see klyde_edge/ui_config/docs/right_rail_and_canvas_ux_direction.md
 *
 * @layer presentation
 * @arch_component_type registry
 * @arch_domain canvas
 */

import { DetailsCard } from '@/components/copilot/rail-insights/details-card';
import { RfqHealthCard } from '@/components/copilot/rail-insights/rfq-health-card';
import { ObeyaKpiCard } from './obeya/kpi-card';
import { ObeyaAttentionCard } from './obeya/attention-card';
import { ObeyaHealthCard } from './obeya/health-card';
import { ObeyaQualityCard } from './obeya/quality-card';
import { AgentNarrativeCard } from './obeya/agent-narrative-card';
import { AgentGraphCanvasCard } from './web/agent-graph-canvas-card';
import { ObeyaSnapshotCard } from './obeya/snapshot-card';
import { ObeyaBoardCard } from './obeya/board-card';
import { ObeyaTimelineCard } from './obeya/timeline-card';
import { ObeyaGraphCard } from './obeya/graph-card';
import { ObeyaCostPlanGraphCard } from './obeya/costplan-graph-card';
import { ObeyaCostPlanKpisCard } from './obeya/costplan-kpi-row-card';
import { ObeyaCostPlanOutputCard } from './obeya/costplan-output-card';
import { ObeyaCostPlanVersionsCard } from './obeya/costplan-versions-bar-card';
import { ObeyaCostPlanHeaderCard } from './obeya/costplan-header-card';
import { ObeyaCostPlanReviewHeaderCard } from './obeya/costplan-review-header-card';
import { ObeyaCostPlanReviewGroupsCard } from './obeya/costplan-review-groups-card';
import { ObeyaCostPlanReviewSelectionsCard } from './obeya/costplan-review-selections-card';
import { ObeyaCostPlanReviewApprovalsCard } from './obeya/costplan-review-approvals-card';
import { ObeyaCostPlanReviewPlanChangesCard } from './obeya/costplan-review-plan-changes-card';
import { ObeyaCostPlanLinesCard } from './obeya/costplan-lines-card';
import { ObeyaCostPlanRoomsCard } from './obeya/costplan-rooms-card';
import { ObeyaCostPlanComparisonCard } from './obeya/costplan-comparison-card';
import { ObeyaRoomComparisonCard } from './obeya/room-comparison-card';
import { ObeyaRoomStoryCard } from './obeya/room-story-card';
import { ObeyaPlanPositionHeaderCard } from './obeya/plan-position-header-card';
import { ObeyaPlanLensKpiStripCard } from './obeya/plan-lenses/plan-lens-kpi-strip-card';
import { ObeyaPlanLensGapCard } from './obeya/plan-lenses/plan-lens-gap-card';
import { ObeyaPlanLensVendorsCard } from './obeya/plan-lenses/plan-lens-vendors-card';
import { ObeyaPlanLensNarrativeCard } from './obeya/plan-lenses/plan-lens-narrative-card';
import { ObeyaPlanLensConcentrationCard } from './obeya/plan-lenses/plan-lens-concentration-card';
import { ObeyaPlanLensDecisionsCard } from './obeya/plan-lenses/plan-lens-decisions-card';
import { ObeyaPlanLensFirmnessCard } from './obeya/plan-lenses/plan-lens-firmness-card';
import { ObeyaPlanLensFirmnessSelectedCard } from './obeya/plan-lenses/plan-lens-firmness-selected-card';
import { ObeyaPlanLensFirmnessOpenCard } from './obeya/plan-lenses/plan-lens-firmness-open-card';
import { ObeyaPlanLensRoomsCard } from './obeya/plan-lenses/plan-lens-rooms-card';
import { ObeyaPlanLensRoomsNarrativeCard } from './obeya/plan-lenses/plan-lens-rooms-narrative-card';
import { ObeyaPlanPositionRoomsCard } from './obeya/plan-position-rooms-card';
import {
  ObeyaPlanCommercialKpiSpendCard, ObeyaPlanCommercialKpiLinesCard, ObeyaPlanCommercialKpiVendorsCard,
} from './obeya/plan-commercial/kpi-cards';
import { ObeyaPlanCommercialNarrativeCard } from './obeya/plan-commercial/narrative-card';
import {
  ObeyaPlanCommercialVendorsCard, ObeyaPlanCommercialProductsCard,
} from './obeya/plan-commercial/vendors-products-cards';
import { ObeyaCostPlanRfqsCard } from './obeya/costplan-rfqs-card';
import { ObeyaPortfolioCard } from './obeya/portfolio-card';
import { ObeyaMatrixCard } from './obeya/matrix-card';
// Borrowed from the explorer surface (same card, two surfaces) so capabilities
// whose answer is a project→RFQ tree (rfi_explorer) can mount it canvas-side.
import { ObeyaProjectListCard } from '../../explorer/cards/obeya/project-list-card';
import { MatrixCard } from './shared/matrix-card';
import { HomeHeroCard } from './home/hero-card';
import { ModeTileCard } from './home/mode-tile-card';
import { HomeAttentionCard } from './home/attention-card';
import { HomePipelineCard } from './home/pipeline-card';
import { HomeRecentCard } from './home/recent-card';
import { IntentResultsCard } from './intent/results-card';
import { IntentPdfIngestCard } from './intent/pdf-ingest-card';
import { IntentLinePicksCard } from './intent/line-picks-card';
import { AddWishCard } from './discovery/add-wish-card';
import { VendorCatalogCard } from './vendors/catalog-card';
import { ProductSearchResultsCard } from './products/search-results-card';
// mode11-only EXPERIMENTAL fork — see ui_config/composition/cards/vendors.py's
// product_search_results_hybrid docstring.
import { ProductSearchResultsHybridCard } from './products/search-results-card-hybrid';
import { CategoryCatalogCard } from './products/category-catalog-card';
import { FavouritesGridCard } from './products/favourites-grid-card';
import { CustomProductsGridCard } from './products/custom-products-grid-card';
import { PricingBenchmarkLadderCard } from './products/pricing-benchmark-ladder-card';
// Investor Relations (mode10) — per-layout canvas cards (mode9 pattern:
// thin cards reading one shared hook, placed by per-kind presets).
import { ContentFaqGridCard } from './investor_relations/content-faq-grid-card';
import { ContentComparisonCard } from './investor_relations/content-comparison-card';
import { ContentLetterCard } from './investor_relations/content-letter-card';
import { ContentAnswerMount } from './investor_relations/content-answer-mount';
import { AskProseCard } from './ask/prose-card';
import { AskQuickAnswerCard } from './ask/quick-answer-card';
import { AskRendererSlotCard } from './ask/renderer-slot-card';
import { AddVendorCard } from './vendors/add-vendor-card';
import { ProductDetailCard } from './vendors/product-detail-card';
import { ProductSummaryStripCard } from './vendors/product-summary-strip-card';
import { ProductHeroCard } from './vendors/product-hero-card';
import { ProductDescriptionCard } from './vendors/product-description-card';
import { ProductSpecsCard } from './vendors/product-specs-card';
import { ProductDocumentsCard } from './vendors/product-documents-card';
import { ProductActionsCard } from './vendors/product-actions-card';
import { SellerLandingCard } from './seller/seller-landing-card';
import { VendorQuoteFormCard } from './seller/vendor-quote-form-card';
import { SellerResponseQuadrantCard } from './seller/seller-response-quadrant-card';
import { CostForecastBurnCard } from './finops/cost-forecast-burn-card';
import { CostTimeToTargetCard, CostTtsHeaderCard, CostTtsChartCard, CostTtsKpisCard } from './finops/cost-time-to-target-card';
import { CostMonthlyBreakdownCard } from './finops/cost-monthly-breakdown-card';
import { OpexBurnCard } from './finops/opex/opex-burn-card';
import { OpexCostByMonthCard } from './finops/opex/opex-cost-by-month-card';
import { OpexTiersCard } from './finops/opex/opex-tiers-card';
import { OpexCapacityCard } from './finops/opex/opex-capacity-card';
import { OpexSubscriptionsCard } from './finops/opex/opex-subscriptions-card';
import { OpexNotCostedCard } from './finops/opex/opex-not-costed-card';
import { OpexMonthHeaderCard, OpexMonthDriversCard, OpexMonthSubscriptionsCard } from './finops/opex/opex-month-cards';
import { OpexTierHeaderCard } from './finops/opex/opex-tier-header-card';
import { OpexServiceHistoryCard } from './finops/opex/opex-service-history-card';
import { OpexTierDueCard, OpexNarrativeCard } from './finops/opex/opex-placeholder-cards';
import { OpexAllAssumptionsCard } from './finops/opex/opex-all-assumptions-card';
import { OpexOnboardingCard } from './finops/opex/opex-onboarding-card';
import { OpexVendorsCard } from './finops/opex/opex-vendors-card';
import { OpexVendorHeaderCard, OpexVendorUpkeepCard } from './finops/opex/opex-vendor-cards';
import { ArchCapabilityMapCard } from './arch/arch-capability-map-card';
import { ArchTopologyCard } from './arch/arch-topology-card';
import { ArchTabsCard } from './arch/arch-tabs-card';
import { ArchDetailsCard } from './arch/arch-details-card';
import { WebHeroCard } from './web/web-hero-card';
import { WebMediaShowcaseCard } from './web/web-media-showcase-card';
import { WebPricingSectionCard } from './web/web-pricing-section-card';
import { Canvas2Card } from './web/canvas-2-card';
import { WebBenefitsGridCard } from './web/web-benefits-grid-card';
import { WebTestimonialCard } from './web/web-testimonial-card';
import { WebHowItWorksCard } from './web/web-how-it-works-card';
import { WebBottomCtaCard } from './web/web-bottom-cta-card';
import { WebTopNavCard } from './web/web-top-nav-card';
import { WebFooterCard } from './web/web-footer-card';
import { WebLandingFoundersCard } from './web/web-landing-founders-card';
import { WebComparisonStripCard } from './web/web-comparison-strip-card';
import { WebBlogIndexCard } from './web/web-blog-index-card';
import { WebQAInputCard } from './web/web-qa-input-card';
import { WebQARendererSlotCard } from './web/web-qa-renderer-slot-card';
import { DemoAskInputCard } from './web/demo-ask-input-card';
import { DemoAskRendererSlotCard } from './web/demo-ask-renderer-slot-card';
import { DemoTestRunHeaderCard } from './web/demo-test-run-header-card';
import { DemoSceneProgressionCard } from './web/demo-scene-progression-card';
import { DemoTokenomicsCard } from './web/demo-tokenomics-card';
import { DemoWeightsCard } from './web/demo-weights-card';
import { DemoRulesCard } from './web/demo-rules-card';
import { DemoFrontierCard } from './web/demo-frontier-card';
import { DemoRecordingCard } from './web/demo-recording-card';
import { DemoExtractedLinesCard } from './web/demo-extracted-lines-card';
import { WebQAShortAnswerCard } from './web/web-qa-short-card';
import { WebQALongAnswerCard } from './web/web-qa-long-card';
import { WebQALlmAnswerCard } from './web/web-qa-llm-card';
import { WebQASourceDocCard } from './web/web-qa-source-doc-card';
import { WebQAFollowupsCard } from './web/web-qa-followups-card';
import { WebQASourcesCard } from './web/web-qa-sources-card';
import { WebQAGroundingCard } from './web/web-qa-grounding-card';
import { WebQACapabilityCard } from './web/web-qa-capability-card';
import { WebQAConversationCard } from './web/web-qa-conversation-card';
import { WebQASourceCompareCard } from './web/web-qa-source-compare-card';
// mode9 — System Obeya (cards-everywhere port of legacy system-obeya-widget)
import { SysObeyaKpiFilesCard } from './system_obeya/kpi-files-card';
import { SysObeyaKpiFeaturesCard } from './system_obeya/kpi-features-card';
import { SysObeyaKpiCapabilitiesCard } from './system_obeya/kpi-capabilities-card';
import { SysObeyaKpiProductivityCard } from './system_obeya/kpi-productivity-card';
import { SysObeyaModuleTableCard } from './system_obeya/module-table-card';
import { SysObeyaProductivityChartCard } from './system_obeya/productivity-chart-card';
import { SysObeyaTeamComparisonCard } from './system_obeya/team-comparison-card';
// mode9 — sel:high_cc_functions (Complexity Hotspots)
import { SysObeyaKpiHighCcCountCard } from './system_obeya/kpi-high-cc-count-card';
import { SysObeyaKpiAvgCcCard } from './system_obeya/kpi-avg-cc-card';
import { SysObeyaKpiHighestCcCard } from './system_obeya/kpi-highest-cc-card';
import { SysObeyaKpiModulesAffectedCard } from './system_obeya/kpi-modules-affected-card';
import { SysObeyaHighCcModulesTableCard } from './system_obeya/high-cc-modules-table-card';
import { SysObeyaHighCcFunctionsTableCard } from './system_obeya/high-cc-functions-table-card';
// mode9 — sel:cohesion (Procedural Patterns)
import { SysObeyaKpiCohesionFlaggedCard } from './system_obeya/kpi-cohesion-flagged-card';
import { SysObeyaKpiCohesionHighCard } from './system_obeya/kpi-cohesion-high-card';
import { SysObeyaKpiCohesionAvgCard } from './system_obeya/kpi-cohesion-avg-card';
import { SysObeyaKpiCohesionModulesCard } from './system_obeya/kpi-cohesion-modules-card';
import { SysObeyaCohesionModulesTableCard } from './system_obeya/cohesion-modules-table-card';
import { SysObeyaCohesionRecommendationsCard } from './system_obeya/cohesion-recommendations-card';
// Generic LLM synopsis card — works for any system_obeya view via view_name prop.
import { SysObeyaSynopsisCard } from './system_obeya/synopsis-card';
// Analytics — capability coverage (mode9 sel:coverage). Composite dashboard
// over analytics_coverage; content_keys are mode-neutral (relocatable).
import {
  AnalyticsCovKpiCoverageCard,
  AnalyticsCovKpiUsedCard,
  AnalyticsCovKpiUnusedCard,
  AnalyticsCovKpiNamespacesCard,
} from './analytics/kpi-cards';
// One list, four perspectives (Map · Ranked · By whom · Dead) — replaces the
// former separate map/by-namespace/by-role/unused-table cards. In-canvas
// perspective toggle; unrelated to the left-rail Explorer surface.
import { AnalyticsCovSurfaceCard } from './analytics/capability-surface-card';

type CanvasCardComponent = React.ComponentType;

export const CANVAS_CARD_REGISTRY: Record<string, CanvasCardComponent> = {
  // mode9 — System Obeya module_summary preset. Top row = 4 KPI tiles
  // (Files, Features, Capabilities, Productivity), each its own card
  // slicing 2 fields off the shared system_obeya_view composite. Main
  // row = table + productivity chart + team comparison.
  sys_obeya_kpi_files: SysObeyaKpiFilesCard,
  sys_obeya_kpi_features: SysObeyaKpiFeaturesCard,
  sys_obeya_kpi_capabilities: SysObeyaKpiCapabilitiesCard,
  sys_obeya_kpi_productivity: SysObeyaKpiProductivityCard,
  sys_obeya_module_table: SysObeyaModuleTableCard,
  sys_obeya_productivity_chart: SysObeyaProductivityChartCard,
  sys_obeya_team_comparison: SysObeyaTeamComparisonCard,
  // mode9 — sel:high_cc_functions preset. 4 KPI tiles + Module Breakdown
  // + Function Detail. All slice the same composite (view_name=high_cc_functions).
  sys_obeya_kpi_high_cc_count: SysObeyaKpiHighCcCountCard,
  sys_obeya_kpi_avg_cc: SysObeyaKpiAvgCcCard,
  sys_obeya_kpi_highest_cc: SysObeyaKpiHighestCcCard,
  sys_obeya_kpi_modules_affected: SysObeyaKpiModulesAffectedCard,
  sys_obeya_high_cc_modules_table: SysObeyaHighCcModulesTableCard,
  sys_obeya_high_cc_functions_table: SysObeyaHighCcFunctionsTableCard,
  // mode9 — sel:cohesion (Procedural Patterns)
  sys_obeya_kpi_cohesion_flagged: SysObeyaKpiCohesionFlaggedCard,
  sys_obeya_kpi_cohesion_high: SysObeyaKpiCohesionHighCard,
  sys_obeya_kpi_cohesion_avg: SysObeyaKpiCohesionAvgCard,
  sys_obeya_kpi_cohesion_modules: SysObeyaKpiCohesionModulesCard,
  sys_obeya_cohesion_modules_table: SysObeyaCohesionModulesTableCard,
  sys_obeya_cohesion_recommendations: SysObeyaCohesionRecommendationsCard,
  sys_obeya_synopsis: SysObeyaSynopsisCard,
  // mode9 — sel:coverage (Capability Coverage). KPI strip + capability
  // map (hero) + by-namespace / by-whom + unused table. All slice one
  // analytics_coverage composite.
  analytics_cov_kpi_coverage: AnalyticsCovKpiCoverageCard,
  analytics_cov_kpi_used: AnalyticsCovKpiUsedCard,
  analytics_cov_kpi_unused: AnalyticsCovKpiUnusedCard,
  analytics_cov_kpi_namespaces: AnalyticsCovKpiNamespacesCard,
  analytics_cov_surface: AnalyticsCovSurfaceCard,
  rfi_details: DetailsCard,
  // Obeya carry-over overview tiles — co-mounted on the Overview preset.
  obeya_kpi: ObeyaKpiCard,
  obeya_attention: ObeyaAttentionCard,
  obeya_health: ObeyaHealthCard,
  obeya_quality: ObeyaQualityCard,
  // Mode-neutral content keys — the new project_health and
  // project_data_quality BFFs declare these as their renderer_card.
  // Same React components as the obeya_* keys above (cards-everywhere:
  // primitives are shared, content_key names follow the BFF that
  // produces the data). Both keys coexist during cohabitation.
  project_health: ObeyaHealthCard,
  project_data_quality: ObeyaQualityCard,
  // rfq_health BFF declares renderer_card="rfq_health" — same component
  // the rail mounts for sel:rfq, just registered in the canvas registry
  // too so mode8's dispatched-card slot can mount it. SynthesisCard
  // (the body underneath) already handles dual-context (useTurn() in
  // transcript / useViewportSelection() on the rail).
  rfq_health: RfqHealthCard,
  // Agent decision surface — stacked above whichever data card ki_ask's
  // dispatch picked (typically obeya_health). Reads narrative + cited
  // rule + recommendations + audit provenance from useDemoAskStore.
  // Renders nothing when payload.narrative is absent (depth=data calls,
  // non-synthesis capabilities).
  agent_narrative: AgentNarrativeCard,
  // Default interactive fallback for ki_ask responses when the picked
  // capability has no purpose-built renderer (or the named card isn't
  // registered). Routes through graph_<entity_type>_visualize to get
  // {nodes, edges} for the resolved entity and renders via reagraph —
  // always-something-to-explore property without authoring per-capability
  // cards. See demo-ask-renderer-slot-card fallback logic.
  agent_graph: AgentGraphCanvasCard,
  obeya_snapshot: ObeyaSnapshotCard,
  // Full-bleed views — one card per preset, mounted via the preset
  // switcher chip strip. Same data the overview tiles consume
  // (`obeya.detail`); React Query dedupes the fetch.
  obeya_board: ObeyaBoardCard,
  obeya_timeline: ObeyaTimelineCard,
  obeya_graph: ObeyaGraphCard,
  // Project→RFQ tree, borrowed from the explorer surface (rfi_explorer's card).
  obeya_project_list: ObeyaProjectListCard,
  // No-selection paint — project portfolio listing.
  obeya_portfolio: ObeyaPortfolioCard,
  // RFQ-scoped vendor comparison matrix.
  obeya_matrix: ObeyaMatrixCard,
  // Generic `MatrixCard` parameterised for the RFQ matrix. The same
  // renderer fronts any BFF strategy that ships `MatrixResponse`
  // (future vendor_matrix / costplan_matrix / etc. add a registry
  // entry here, no new component). Cohabits with `obeya_matrix`
  // (legacy 738-line widget) until that retires.
  rfq_matrix: () => (
    <MatrixCard
      strategy="rfq_matrix"
      entityTypes={['rfq']}
      paramKey="rfq_sid"
      emptyStateMessage="Select an RFQ to see the matrix"
    />
  ),
  // Overview KPI row — same costplan_details payload as the pivot
  // table below it, second card slicing one composite response. The
  // old obeya_costplan detail card (mega card: name/dates/notes/
  // line-items/actions) was byte-identical to the rail's mcp_details
  // mount of the same EntityDetailsCard component — retired, nothing
  // lost, that content lives in the rail alone now.
  obeya_costplan_kpis: ObeyaCostPlanKpisCard,
  // One card wrapping the KPI row + versions bar (see its own docstring)
  // — replaces the kpis/versions PAIR of placements on both cost-plan
  // presets; the two standalone entries above/below stay registered in
  // case anything still points at them directly, but mode1.py no
  // longer does.
  obeya_costplan_header: ObeyaCostPlanHeaderCard,
  obeya_costplan_output: ObeyaCostPlanOutputCard,
  // Save & compare versions bar — sits between the KPI row and the pivot
  // table on mode1_costplan. Its "Compare to current" row action only
  // sets compareRevId (costplan-compare-store) — Watch's two-column
  // totals read it directly, and Watch's own "Review changes" trigger
  // is what opens the review-changes overlay below.
  obeya_costplan_versions: ObeyaCostPlanVersionsCard,
  // Review-changes overlay (Concertina) — mode1_costplan's
  // overlay_header_card / overlay_body_cards, opened via openMatrix().
  obeya_costplan_review_header: ObeyaCostPlanReviewHeaderCard,
  obeya_costplan_review_groups: ObeyaCostPlanReviewGroupsCard,
  obeya_costplan_review_selections: ObeyaCostPlanReviewSelectionsCard,
  obeya_costplan_review_approvals: ObeyaCostPlanReviewApprovalsCard,
  obeya_costplan_review_plan_changes: ObeyaCostPlanReviewPlanChangesCard,
  // The pivot table IS the landing preset's main content now (folded
  // in, not a separate "Table" tab) — mode1_costplan places it
  // directly below the KPI strip.
  obeya_costplan_lines: ObeyaCostPlanLinesCard,
  // Room-allocation matrix — lines × room-programme slots, drag-select +
  // type-to-fill. Second preset in mode1's "mcp" selection list.
  obeya_costplan_rooms: ObeyaCostPlanRoomsCard,
  obeya_costplan_comparison: ObeyaCostPlanComparisonCard,
  obeya_room_comparison: ObeyaRoomComparisonCard,
  obeya_room_story: ObeyaRoomStoryCard,
  // Plan Position — merged Room Story + Plan Commercial, one tab.
  // Supersedes obeya_room_story + the obeya_plan_commercial_* row
  // below on mode1's canvas (mode1_plan_position); both stay
  // registered (unmounted, harmless) rather than deleted this pass.
  obeya_plan_position_header: ObeyaPlanPositionHeaderCard,
  obeya_plan_position_rooms: ObeyaPlanPositionRoomsCard,
  obeya_plan_lens_kpi_strip: ObeyaPlanLensKpiStripCard,
  obeya_plan_lens_gap: ObeyaPlanLensGapCard,
  obeya_plan_lens_vendors: ObeyaPlanLensVendorsCard,
  obeya_plan_lens_narrative: ObeyaPlanLensNarrativeCard,
  obeya_plan_lens_concentration: ObeyaPlanLensConcentrationCard,
  obeya_plan_lens_decisions: ObeyaPlanLensDecisionsCard,
  obeya_plan_lens_firmness: ObeyaPlanLensFirmnessCard,
  obeya_plan_lens_firmness_selected: ObeyaPlanLensFirmnessSelectedCard,
  obeya_plan_lens_firmness_open: ObeyaPlanLensFirmnessOpenCard,
  obeya_plan_lens_rooms: ObeyaPlanLensRoomsCard,
  obeya_plan_lens_rooms_narrative: ObeyaPlanLensRoomsNarrativeCard,
  obeya_plan_commercial_kpi_spend: ObeyaPlanCommercialKpiSpendCard,
  obeya_plan_commercial_kpi_lines: ObeyaPlanCommercialKpiLinesCard,
  obeya_plan_commercial_kpi_vendors: ObeyaPlanCommercialKpiVendorsCard,
  obeya_plan_commercial_narrative: ObeyaPlanCommercialNarrativeCard,
  obeya_plan_commercial_vendors: ObeyaPlanCommercialVendorsCard,
  obeya_plan_commercial_products: ObeyaPlanCommercialProductsCard,
  obeya_costplan_rfqs: ObeyaCostPlanRfqsCard,
  // Same entity, graph view — second preset in mode1's "mcp" selection
  // list, same tab-switcher pattern product/project already use for
  // mode1_graph.
  obeya_costplan_graph: ObeyaCostPlanGraphCard,
  // Home cards — mode2's landing surface (cards-everywhere
  // replacement for the legacy <KlydeLandingWidget />). Same data
  // sources, decomposed into separately-mountable cards.
  home_hero: HomeHeroCard,
  mode_tile: ModeTileCard,
  home_attention: HomeAttentionCard,
  home_pipeline: HomePipelineCard,
  home_recent: HomeRecentCard,
  // Intent (mode3) — full-bleed results card. Internal store-driven
  // dispatch between schedule-grid lanes (gridResponse) and single-
  // query product grid (response). Mode3-native, no legacy widget
  // imports.
  intent_results: IntentResultsCard,
  // Count-chip expand view — real picks for one line, read-only.
  // Mounted via the legacy openWith(turn) path (no viewport-selection
  // dependency); reads its target line from focused-picks-line-store.
  intent_line_picks: IntentLinePicksCard,
  // Full-bleed PDF schedule ingest form — mode3's `mode3_add_schedule`
  // preset paints this when the explorer fires the `add_schedule`
  // selection. Mode3-native form, no AddScheduleFormWidget import.
  intent_pdf_ingest: IntentPdfIngestCard,
  // Buyer-wish "Add custom item" form — full-bleed canvas card,
  // BFF-driven: server ships vocab + labels via `wish_form_meta`,
  // validates server-side and returns `field_errors`, persists via
  // `catalog_create_wish` (Neo4j virtual Product + WANTS edge + media).
  // No mode/preset wiring lives here — composition decides placement
  // via PlacementSpec(content_key="add_wish", ...).
  add_wish: AddWishCard,
  // Vendors (mode4) — full-bleed vendor catalog (collections + product
  // grid). Mode4-native: doesn't import VendorCollectionsWidget.
  vendor_catalog: VendorCatalogCard,
  // Products (mode4) search — store-driven results grid; reuses the browse
  // ProductTile. Paints when the mode4 selection is `search`.
  product_search_results: ProductSearchResultsCard,
  product_search_results_hybrid: ProductSearchResultsHybridCard,
  category_catalog: CategoryCatalogCard,
  favourites_grid: FavouritesGridCard,
  custom_products_grid: CustomProductsGridCard,
  // Catalogue-wide L/M/H ladder — mode11's 'Benchmarks' explorer row
  // (sel:benchmarks). No selection required.
  pricing_benchmark_ladder: PricingBenchmarkLadderCard,
  // Investor Relations (mode10) — one card per layout, placed by its own
  // per-kind preset (faq grid / comparison / letter). Each reads the shared
  // use-content-set hook + the selected topic; content_answer is the thin
  // block-renderer they mount internally (data-driven count, not preset-
  // placeable). Layout density = which card the preset places, not a branch.
  content_faq_grid: ContentFaqGridCard,
  content_comparison: ContentComparisonCard,
  content_letter: ContentLetterCard,
  // Standalone single answer — mounted by renderer_card="content_answer"
  // (Ask Klyde) with the matched key as entityId; self-fetches that one entry.
  content_answer: ContentAnswerMount,
  ask_prose: AskProseCard,
  ask_quick_answer: AskQuickAnswerCard,
  ask_renderer_slot: AskRendererSlotCard,
  // Vendors (mode4) — full-bleed Add Vendor form. Mounts when the
  // explorer fires the `add_vendor` selection.
  add_vendor: AddVendorCard,
  // Vendors (mode4) — concertina-overlay product detail. Same data
  // and schema as the rail VendorBriefCard for sel:product; this is
  // the canvas-scale paint mounted when the rail's [⤢ Expand]
  // affordance pushes a turn. Legacy monolith — kept for cohabitation
  // while the cards-everywhere decomposition below proves out.
  product_detail: ProductDetailCard,
  // Vendors (mode4) — cards-everywhere PDP decomposition. Each card
  // slices `FullProductDetail` from the shared `graph_product_explore`
  // round-trip (React Query dedups by (strategy, semantic_id), so all
  // three render from ONE network call). Composed into the concertina
  // overlay via mode4_product.overlay_header_card / overlay_body_cards.
  product_summary_strip: ProductSummaryStripCard,
  product_hero: ProductHeroCard,
  product_description: ProductDescriptionCard,
  product_specs: ProductSpecsCard,
  product_documents: ProductDocumentsCard,
  product_actions: ProductActionsCard,
  // Vendor portal (mode5) — no-selection landing dashboard
  // (welcome + summary tiles). Replaces the legacy mode's passive
  // "select an item" empty canvas with a real first-paint surface.
  seller_landing: SellerLandingCard,
  // Vendor portal (mode5) — full-bleed quote submission form when
  // an RFQ / vendor_response is selected. Phase 1 thin shim
  // re-exporting `<VendorQuoteFormWidget />`; the card spec is the
  // contract, runtime can be hot-swapped under it.
  vendor_quote_form: VendorQuoteFormCard,
  // Vendor portal (mode5) — prescriptive 4-cell quadrant (urgency ×
  // catalogue-fit). Default canvas for mode5 once it ships. Reads
  // `vendor_portal_priority_list` (server-bucketed). Layer 1 chrome
  // routes through `useSellerLabel`.
  seller_response_quadrant: SellerResponseQuadrantCard,
  // FinOps (mode6) — Monte Carlo bootstrap forecast canvas. Reads
  // `cost_forecast_burn` (component-aware bands per fixed/gemini/
  // vertex + total). Recharts ComposedChart: line per series for
  // historical + P50, stacked Area pair for the P5–P95 band on the
  // projection portion only. Vertical reference line marks the
  // historical → forecast transition.
  cost_forecast_burn_canvas: CostForecastBurnCard,
  // FinOps (mode6) — Time-to-target Monte Carlo: bell-curve histogram
  // of "days until cumulative burn first crosses CHF threshold". Same
  // bootstrap simulations as the forecast card; different projection
  // of the trajectory tensor — answers "when do we hit X" instead of
  // "where will we be by T". P5/P50/P95 vertical reference lines
  // overlay the histogram.
  cost_time_to_target_canvas: CostTimeToTargetCard,
  cost_tts_header: CostTtsHeaderCard,
  cost_tts_chart: CostTtsChartCard,
  cost_tts_kpis: CostTtsKpisCard,
  // FinOps (mode6) — Token-mix-by-model + monthly-cost view. Stacked
  // bars per :Model on the left axis (tokens, M/K-formatted) + line
  // on the right axis (monthly CHF P50) with P5–P95 ribbon over the
  // projected portion. Historical/projected split at today's calendar
  // month, marked by a vertical reference line. Backward-looking
  // decomposition counterpart to the forecast card.
  cost_monthly_breakdown_canvas: CostMonthlyBreakdownCard,
  // mode6 Opex presets (overview, month, tier, service) — one deduped opex_today fetch (composite payload).
  opex_burn: OpexBurnCard,
  opex_cost_by_month: OpexCostByMonthCard,
  opex_tiers: OpexTiersCard,
  opex_capacity: OpexCapacityCard,
  opex_subscriptions: OpexSubscriptionsCard,
  opex_not_costed: OpexNotCostedCard,
  opex_month_header: OpexMonthHeaderCard,
  opex_month_drivers: OpexMonthDriversCard,
  opex_month_subscriptions: OpexMonthSubscriptionsCard,
  opex_tier_header: OpexTierHeaderCard,
  opex_service_history: OpexServiceHistoryCard,
  opex_tier_due: OpexTierDueCard,
  opex_narrative: OpexNarrativeCard,
  opex_onboarding: OpexOnboardingCard,
  opex_vendors: OpexVendorsCard,
  opex_vendor_header: OpexVendorHeaderCard,
  opex_vendor_upkeep: OpexVendorUpkeepCard,
  // Concertina overlay body on every Opex preset (overlay_body_cards).
  opex_all_assumptions: OpexAllAssumptionsCard,
  // Architecture graph views — the code-intelligence graph as cards.
  // arch_canvas_canvas = capability map (packages→capabilities→features→
  // stories, reagraph), arch_flow_canvas = package-dependency topology.
  // Self-fetch via arch_canvas / arch_flow. Mountable into the renderer
  // slot ("show me the system map"); placeable in a docs/system-obeya
  // modeN. (The tree view stays in arch-explorer.tsx, explorer-side.)
  // Architecture graph views — card copies of the legacy arch widgets
  // (cards/arch/*), self-sufficient height (min-h-[480px] internally) so
  // they survive a bare Canvas2Card mount. Originals stay for the legacy
  // docs mode until it's retired.
  arch_canvas_canvas: ArchCapabilityMapCard,
  arch_flow_canvas: ArchTopologyCard,
  // Tabbed wrapper — single placement slot, internal toggle picks
  // tree vs flow. Used by mode7/reception where the two-up layout
  // cramped both viz cards. Either tab populates the details panel
  // below via useGraphSelectionStore (selection-channel unchanged).
  arch_tabs_canvas: ArchTabsCard,
  // selection-driven inspector — clicking a node in either graph card
  // (both write useGraphSelectionStore) populates this.
  arch_details_canvas: ArchDetailsCard,
  // mode7 (Landing) — first cards-everywhere port of klyde-website.
  // Eyebrow + two-line headline + body + CTA + fineprint + 4-tile
  // stats strip. Content sourced from web_landing.yaml via the
  // web_landing_hero BFF strategy. Visual structure ported from
  // klyde-website/src/components/landing/Hero.tsx.
  web_hero_card_canvas: WebHeroCard,
  // Media showcase kind — a full-bleed graphic (video / react_component)
  // framed by short prose. Not yet mounted in a live mode7 selection;
  // the capability itself (scaffold -> BFF -> MediaRenderer) is
  // testable via these two content_keys directly.
  web_how_it_works_discovery_video_canvas: () => (
    <WebMediaShowcaseCard strategyAction="web_how_it_works_discovery_video" />
  ),
  web_how_it_works_foundation_canvas: () => (
    <WebMediaShowcaseCard strategyAction="web_how_it_works_foundation" />
  ),
  // mode7 (Landing) — three-tier pricing comparison (Klyde Lite /
  // Pro / Edge). Stacks under the hero in canvas_2's container.
  // Visual structure ported from
  // klyde-website/src/components/landing/PricingSection.tsx.
  web_pricing_section_canvas: WebPricingSectionCard,
  // canvas_2 — card-in-card container. Mounts other cards by
  // content_key per its YAML-authored placement list. mode7 mounts
  // exactly one of these full-bleed; the container handles the
  // page's vertical layout internally. Validates panels-as-cards
  // recursion without touching the IPE shell.
  canvas_2_card: () => <Canvas2Card strategyAction="web_landing_container" />,
  // mode7 /for-buyers — second selection in mode7. Same Canvas2Card
  // renderer as the landing's container; distinct content_key + a
  // distinct `strategyAction` prop drive it to fetch the for-buyers
  // placements instead of the landing's.
  web_for_buyers_container_canvas: () => <Canvas2Card strategyAction="web_for_buyers_container" />,
  // /for-buyers sections — benefits grid (8 lucide-icon tiles),
  // single-quote testimonial, 5-step how-it-works, mailto bottom CTA.
  // All four use the WEB_SECTION typography bundle.
  web_for_buyers_benefits_canvas: WebBenefitsGridCard,
  web_for_buyers_testimonial_canvas: WebTestimonialCard,
  web_for_buyers_how_it_works_canvas: WebHowItWorksCard,
  web_for_buyers_bottom_cta_canvas: WebBottomCtaCard,
  // mode7 chrome — header (region='top') and footer (region='bottom')
  // are panel-as-card placements declared at the preset level. Both are
  // self-contained (hardcoded React, no BFF strategy) until the source
  // Header / Footer full surfaces port and a content-driven schema
  // earns its place.
  web_top_nav_canvas: WebTopNavCard,
  web_footer_canvas: WebFooterCard,
  // Landing-only sections — reuses of existing kinds (parameterised
  // via strategyAction) plus one new component (founders).
  web_landing_personas_canvas: () => (
    <WebBenefitsGridCard strategyAction="web_landing_personas" />
  ),
  web_landing_how_it_works_canvas: () => (
    <WebHowItWorksCard strategyAction="web_landing_how_it_works" />
  ),
  web_landing_founders_canvas: WebLandingFoundersCard,
  web_landing_bottom_cta_canvas: () => (
    <WebBottomCtaCard strategyAction="web_landing_bottom_cta" />
  ),
  // klyde10-aligned landing sections — shared renderers with a
  // strategyAction override; only the comparison strip is new.
  web_landing_quote_canvas: () => (
    <WebTestimonialCard strategyAction="web_landing_quote" />
  ),
  web_landing_workflow_canvas: WebComparisonStripCard,
  web_landing_search_to_ask_canvas: () => (
    <WebBenefitsGridCard strategyAction="web_landing_search_to_ask" />
  ),
  web_landing_discovery_canvas: () => (
    <WebBenefitsGridCard strategyAction="web_landing_discovery" />
  ),
  web_landing_use_cases_canvas: () => (
    <WebBenefitsGridCard strategyAction="web_landing_use_cases" />
  ),
  web_landing_three_buys_canvas: () => (
    <WebBenefitsGridCard strategyAction="web_landing_three_buys" />
  ),
  web_landing_edge_canvas: () => (
    <WebBenefitsGridCard strategyAction="web_landing_edge" />
  ),
  // /for-suppliers — third selection in mode7. Reuses parameterised
  // hero / benefits-grid / testimonial / how-it-works / bottom-cta
  // renderers; container shares Canvas2Card with strategyAction prop.
  web_for_suppliers_container_canvas: () => (
    <Canvas2Card strategyAction="web_for_suppliers_container" />
  ),
  web_for_suppliers_hero_canvas: () => (
    <WebHeroCard strategyAction="web_for_suppliers_hero" />
  ),
  web_for_suppliers_benefits_canvas: () => (
    <WebBenefitsGridCard strategyAction="web_for_suppliers_benefits" />
  ),
  web_for_suppliers_testimonial_canvas: () => (
    <WebTestimonialCard strategyAction="web_for_suppliers_testimonial" />
  ),
  web_for_suppliers_how_it_works_canvas: () => (
    <WebHowItWorksCard strategyAction="web_for_suppliers_how_it_works" />
  ),
  web_for_suppliers_bottom_cta_canvas: () => (
    <WebBottomCtaCard strategyAction="web_for_suppliers_bottom_cta" />
  ),
  // /resources — fourth selection in mode7. Page-intro hero (left-aligned
  // variant of WebHeroCard) + WebBlogIndexCard (featured zone + grid).
  web_resources_container_canvas: () => (
    <Canvas2Card strategyAction="web_resources_container" />
  ),
  web_resources_hero_canvas: () => (
    <WebHeroCard strategyAction="web_resources_hero" />
  ),
  web_resources_blog_index_canvas: () => (
    <WebBlogIndexCard strategyAction="web_resources_blog_index" />
  ),
  // /reception — Klyde Reception (agent-driven Q&A). Container shares
  // Canvas2Card. The three child cards are interactive widgets driven
  // by useDocsStore + docs.ask/init — no static content, no per-card
  // strategyAction. The renderer-slot card uses the dispatched-card-slot
  // pattern (looks up CANVAS_CARD_REGISTRY at runtime from the agent's
  // response, mounts dynamically).
  web_reception_container_canvas: () => (
    <Canvas2Card strategyAction="web_reception_container" />
  ),
  // 2nd canvas region — mode7's LEFT panel (explorer-sized, resizable).
  // Same primitive as the viewport container: Canvas2Card over a
  // placement list (web_reception.py's web_reception_explorer scaffold).
  web_reception_explorer_canvas: () => (
    <Canvas2Card strategyAction="web_reception_explorer" />
  ),
  web_qa_input_canvas: WebQAInputCard,
  web_qa_renderer_slot_canvas: WebQARendererSlotCard,
  // mode8 (Demo) — agentic ask surface. Parallel to Reception's web_qa_*
  // cards: same dispatched-card-slot pattern, different post target
  // (ki_ask), different store (useDemoAskStore). See
  // klyde_edge/ui_config/composition/cards/demo.py.
  demo_ask_input_canvas: DemoAskInputCard,
  demo_ask_renderer_slot_canvas: DemoAskRendererSlotCard,
  // mode8 test-run-canvas — composed inside the renderer slot when a
  // test_runner response is in the store. Header + scene progression +
  // tokenomics. Each is independently mountable: a future surface can
  // pull any one into a different preset by referencing the content_key.
  demo_test_run_header: DemoTestRunHeaderCard,
  demo_scene_progression: DemoSceneProgressionCard,
  demo_weights: DemoWeightsCard,
  demo_rules: DemoRulesCard,
  demo_frontier: DemoFrontierCard,
  demo_recording: DemoRecordingCard,
  demo_extracted_lines: DemoExtractedLinesCard,
  demo_tokenomics: DemoTokenomicsCard,
  // Reception card set — the /docs/ask response decomposed. Each surfaces
  // one slice of DocsResponse: the answer tiers (short / long / llm), the
  // transparency lenses (source-doc → docs.source raw bytes; grounding &
  // cost → usage/timings), and the trailing context (followups, sources).
  // All read from useDocsStore, populated by web_qa_input_canvas.
  web_qa_short_canvas: WebQAShortAnswerCard,
  web_qa_long_canvas: WebQALongAnswerCard,
  web_qa_llm_canvas: WebQALlmAnswerCard,
  web_qa_source_doc_canvas: WebQASourceDocCard,
  web_qa_followups_canvas: WebQAFollowupsCard,
  web_qa_sources_canvas: WebQASourcesCard,
  web_qa_grounding_canvas: WebQAGroundingCard,
  web_qa_capability_canvas: WebQACapabilityCard,
  web_qa_conversation_canvas: WebQAConversationCard,
  web_qa_source_compare_canvas: WebQASourceCompareCard,
  // Future cards drop in here as the matrix grows. Each card is a
  // stand-alone component; selection-awareness lives inside the card,
  // not in this registry.
};
